import { checkPolotnoType } from '@/pages/AdvancedEditor/utils/checkPolotnoType';
import { ElementType } from 'polotno/model/group-model';
import { ImageElementType } from 'polotno/model/image-model';
import { StoreType } from 'polotno/model/store';
import { Page } from '../AdvancedEditor';
import { polotnoTypes } from '../types';

export const countChildrenFromTemplate = (store: StoreType) => {
    const filter = (child: ElementType) => !!child.custom?.template;

    return countElementsAndFilter(store, filter);
};

export const countChildrenByType = (store: StoreType, type?: polotnoTypes) => {
    const filter = type ? (child: ElementType) => isElementOfType(child, type) : undefined;

    return countElementsAndFilter(store, filter);
};

export const countElementsAndFilter = (store: StoreType, filter?: (child: ElementType) => boolean) => {
    return store.pages.reduce((acc, page) => {
        return acc + countPageElementsAndFilter(page, filter);
    }, 0);
};

export const getUsedTemplates = (store: StoreType) => {
    const templateIds = new Set<string>();
    const templateNames = new Set<string>();

    const elements = filterElements(store, (child: ElementType) => !!child.custom?.templateId && !!child.custom?.templateName);

    elements.forEach(element => {
        templateIds.add(element.custom?.templateId);
        templateNames.add(element.custom?.templateName);
    });

    return {
        templateIds: templateIds.size ? Array.from(templateIds) : ['start from blank'],
        templateNames: templateNames.size ? Array.from(templateNames) : ['start from blank'],
    };
};

const filterElements = (store: StoreType, filter: (child: ElementType) => boolean): ElementType[] => {
    return store.pages.reduce((acc, page) => {
        return acc.concat(filterPageElements(page, filter));
    }, [] as ElementType[]);
};

export const getFileName = (url: string) => url.split('/').pop()?.split('?')[0];

export const isGifElement = (element: ElementType): boolean => {
    return checkPolotnoType(element, polotnoTypes.Image) && !!getFileName((element as ImageElementType).src || '')?.endsWith('.gif');
};

export const isVideoElement = (element: ElementType): boolean => {
    return checkPolotnoType(element, polotnoTypes.Video) || isGifElement(element);
};

export const isImageElement = (element: ElementType): boolean => {
    return checkPolotnoType(element, polotnoTypes.Image) && !isGifElement(element);
};

export const isElementOfType = (element: ElementType, type: polotnoTypes): boolean => {
    if (type === polotnoTypes.Video) {
        return isVideoElement(element);
    }
    if (type === polotnoTypes.Image) {
        return isImageElement(element);
    }

    return element.type === type;
};

export const filterPageElements = (page: Page, filter: (child: ElementType) => boolean): ElementType[] => {
    return page.children?.filter(filter) || [];
};

export const countPageElementsAndFilter = (page: Page, filter?: (child: ElementType) => boolean): number => {
    if (!page.children) {
        return 0;
    }

    if (!filter) {
        return page.children.length;
    }

    return page.children.filter(child => {
        return filter(child);
    }).length;
};

export const countElementsByTypeAndPage = (page: Page, type?: polotnoTypes) => {
    if (!page.children) {
        return 0;
    }

    if (!type) {
        return page.children.length;
    }

    return page.children.filter(child => {
        return isElementOfType(child, type);
    }).length;
};
